import { cs } from '../../lib/currency';
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Printer, Loader2, ArrowLeft } from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';
import api from '../../services/api';
import { useBusinessTimezone } from '../../utils/businessTime';
import { useTenant } from '../../context/TenantContext';
import { friendlyInvoiceNumber } from '../../utils/invoiceNumber';
import { InvoiceStatement } from '../../components/admin/invoice/InvoiceStatement';

// iter306x: branded printable invoice page. Opened in a new tab from
// the View dialog. Uses standard window.print() so admins can save as
// PDF via the browser dialog (Cmd/Ctrl+P → Save as PDF). Print stylesheet
// hides toolbar + uses page-friendly layout.
//
// iter306gg: dates render in the academy's business timezone (not the
// printing admin's local zone). For the common "MMMM d, yyyy" pattern
// we route through Intl.DateTimeFormat with `timeZone` so a paid-at
// timestamp doesn't render as the previous day for a printer in PT.
const INTL_OPTS_FOR_FMT = {
  'MMMM d, yyyy': { month: 'long', day: 'numeric', year: 'numeric' },
};
function safeDate(value, fmt = 'MMMM d, yyyy', tz) {
  if (!value) return '—';
  try {
    const d = parseISO(String(value));
    if (!isValid(d)) return String(value);
    const intlOpts = INTL_OPTS_FOR_FMT[fmt];
    if (intlOpts && tz && String(value).length > 10) {
      return new Intl.DateTimeFormat('en-US', { ...intlOpts, timeZone: tz }).format(d);
    }
    return format(d, fmt);
  } catch {
    return String(value);
  }
}

function safeMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0.00';
  return n.toFixed(2);
}

export default function InvoicePrint() {
  const businessTz = useBusinessTimezone();
  const { branding } = useTenant();
  // iter322 — Print header + footer now pull the active tenant's
  // business name so white-label tenants don't get DAS-branded
  // receipts. Falls back to the platform default if branding hasn't
  // resolved yet (rare — only happens during the first paint).
  const tenantBusinessName = branding?.business_name || 'Cricket Academy';
  const tenantDomain =
    (typeof window !== 'undefined' && window.location && window.location.host) || '';
  const { invoiceId } = useParams();
  const [searchParams] = useSearchParams();
  const invoiceTypeHint = searchParams.get('type') || '';
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchInvoice = useCallback(async () => {
    try {
      // iter309b — Use the dedicated single-invoice lookup endpoint,
      // which handles the monthly_invoices.id fallback for multi-MI
      // enrollments AND avoids the 500-row limit on /admin/invoices/all
      // that left some paid rows out of the print page entirely.
      if (invoiceTypeHint) {
        const resp = await api.get(
          `/admin/invoices/${encodeURIComponent(invoiceId)}/lookup`,
          { params: { type: invoiceTypeHint } },
        );
        if (resp.data) {
          setInvoice(resp.data);
          return;
        }
      }
      // Legacy fallback: iterate buckets (covers the no-type-hint case
      // and pre-iter309b deployments still in flight).
      const buckets = ['paid', 'pending', 'overdue', 'void', 'refunded', 'partially_refunded'];
      let found = null;
      for (const status of buckets) {
        const params = new URLSearchParams({ status });
        if (invoiceTypeHint) params.append('invoice_type', invoiceTypeHint);
        const resp = await api.get(`/admin/invoices/all?${params.toString()}`);
        found = (resp.data || []).find((r) => String(r.id) === String(invoiceId));
        if (found) break;
      }
      if (!found) {
        setError('Invoice not found.');
      } else {
        setInvoice(found);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load invoice.');
    } finally {
      setLoading(false);
    }
  }, [invoiceId, invoiceTypeHint]);

  useEffect(() => { fetchInvoice(); }, [fetchInvoice]);

  // iter335b — fetch the base/surcharge refund breakdown so the printed
  // receipt matches the invoice modal exactly.
  const [printBreakdown, setPrintBreakdown] = useState(null);
  useEffect(() => {
    if (!invoice) return;
    const hasRefund = Number(invoice.refunded_amount || 0) > 0.005;
    if (!hasRefund) return;
    const et = invoice.type === 'enrollment' ? 'monthly_invoice' : invoice.entity_type_for_sync;
    const eid = invoice.type === 'enrollment'
      ? (invoice.monthly_invoice_id || invoice.id)
      : invoice.entity_id;
    if (!et || !eid) return;
    let cancelled = false;
    api.get(`/admin/billing/refund-breakdown/${et}/${eid}`)
      .then((r) => { if (!cancelled) setPrintBreakdown(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [invoice]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
      </div>
    );
  }
  if (error || !invoice) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 gap-4">
        <p className="text-rose-700 font-medium">{error || 'Invoice not found.'}</p>
        <button
          onClick={() => window.close()}
          className="px-4 py-2 rounded border border-slate-300 hover:bg-slate-100"
        >
          Close
        </button>
      </div>
    );
  }

  const isPaid = String(invoice.status || '').toLowerCase() === 'paid';
  const isVoid = String(invoice.status || '').toLowerCase() === 'void';
  // iter335b FIX — `invoice.amount` is already the BASE fee (the invoice
  // collectors emit `payment_amount − surcharge`). The old code treated it
  // as the GROSS total and subtracted the surcharge AGAIN, so a $300 fee
  // with a $9 card surcharge rendered as base $291 / total $300 instead of
  // base $300 / surcharge $9 / total $309 (prod receipt pi_3U2yDz).
  const surcharge = Number(invoice.surcharge_amount || 0);
  const base = Number(invoice.amount ?? invoice.total_amount ?? 0);
  const totalAmount = Math.round((base + surcharge) * 100) / 100;
  const discount = Number(invoice.discount_amount || 0);
  const original = Number(invoice.original_amount || 0);

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white">
      {/* Toolbar — hidden on print */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between print:hidden" data-testid="invoice-print-toolbar">
        <button
          onClick={() => window.close()}
          className="inline-flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <button
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium"
          data-testid="invoice-print-trigger"
        >
          <Printer className="h-4 w-4" /> Print / Save as PDF
        </button>
      </div>

      {/* Page */}
      <div className="max-w-3xl mx-auto bg-white shadow-sm print:shadow-none p-10 my-6 print:my-0 print:p-12" data-testid="invoice-print-page">
        {/* Header */}
        <div className="flex justify-between items-start border-b border-slate-200 pb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">{tenantBusinessName}</h1>
            <p className="text-sm text-slate-600 mt-1">Tax Invoice / Receipt</p>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-wider text-slate-500">Status</p>
            <p className={`text-2xl font-bold ${isPaid ? 'text-green-600' : isVoid ? 'text-slate-400 line-through' : 'text-amber-600'}`}>
              {isPaid ? 'PAID' : isVoid ? 'VOID' : String(invoice.status || 'PENDING').replace(/_/g, ' ').toUpperCase()}
            </p>
          </div>
        </div>

        {/* Meta grid */}
        <div className="grid grid-cols-2 gap-x-12 gap-y-4 mt-8 text-sm">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Invoice #</p>
            <p className="font-mono text-slate-900">{friendlyInvoiceNumber(invoice) || invoice.invoice_number || invoice.id}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Date</p>
            <p className="text-slate-900">{safeDate(invoice.invoice_date || invoice.created_at, 'MMMM d, yyyy', businessTz)}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Bill To</p>
            <p className="text-slate-900 font-medium">{invoice.parent_name || '—'}</p>
            {invoice.parent_email && <p className="text-slate-600 text-xs">{invoice.parent_email}</p>}
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Player(s)</p>
            <p className="text-slate-900">{invoice.player_names || '—'}</p>
          </div>
        </div>

        {/* Line items */}
        <table className="w-full mt-10 text-sm">
          <thead>
            <tr className="border-b border-slate-300">
              <th className="text-left py-3 font-semibold text-slate-700 text-xs uppercase tracking-wider">Description</th>
              <th className="text-right py-3 font-semibold text-slate-700 text-xs uppercase tracking-wider">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-slate-200">
              <td className="py-4">
                <p className="font-medium text-slate-900">{invoice.invoice_type === 'uniform' ? 'Uniform order' : (invoice.type_label || invoice.type || 'Item')}</p>
                <p className="text-slate-600 text-xs mt-0.5">{invoice.description || '—'}</p>
                {invoice.billing_period && (
                  <p className="text-slate-500 text-xs mt-0.5">Billing period: {invoice.billing_period}</p>
                )}
              </td>
              <td className="py-4 text-right font-medium text-slate-900 tabular-nums">
                {invoice.accounting?.items?.length ? '' : <>{cs()}{safeMoney(invoice.accounting ? invoice.accounting.fee_total : base + discount)}</>}
              </td>
            </tr>
            {(invoice.accounting?.items || []).map((it, i) => (
              <tr key={i} className="border-b border-slate-200" data-testid={`invoice-print-item-${i}`}>
                <td className="py-3">
                  <p className="font-medium text-slate-900">{it.description}</p>
                  <p className="text-slate-600 text-xs mt-0.5">{it.quantity} × {cs()}{safeMoney(it.unit_price)}</p>
                </td>
                <td className="py-3 text-right font-medium text-slate-900 tabular-nums">{cs()}{safeMoney(it.amount)}</td>
              </tr>
            ))}
            {!invoice.accounting && surcharge > 0 && (
              <tr className="border-b border-slate-200" data-testid="invoice-surcharge-line">
                <td className="py-4">
                  <p className="font-medium text-slate-900">Card processing fee</p>
                  <p className="text-slate-600 text-xs mt-0.5">Pass-through processor surcharge</p>
                </td>
                <td className="py-4 text-right font-medium text-slate-900 tabular-nums">
                  {cs()}{safeMoney(surcharge)}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Totals */}
        <div className="ml-auto w-full max-w-xs mt-6 text-sm space-y-2">
          {invoice.accounting ? (
            <InvoiceStatement accounting={invoice.accounting} title="" testId="invoice-print-statement" />
          ) : (
            <>
          {(original > 0 && original !== base + discount) && (
            <div className="flex justify-between text-slate-500 line-through">
              <span>Original</span><span className="tabular-nums">{cs()}{safeMoney(original)}</span>
            </div>
          )}
          <div className="flex justify-between text-slate-700">
            <span>Subtotal</span><span className="tabular-nums">{cs()}{safeMoney(base + discount)}</span>
          </div>
          {discount > 0 && (
            <div className="flex justify-between text-rose-700">
              <span>Discount{invoice.discount_reason ? ` (${invoice.discount_reason})` : ''}</span>
              <span className="tabular-nums">−{cs()}{safeMoney(discount)}</span>
            </div>
          )}
          {surcharge > 0 && (
            <div className="flex justify-between text-slate-700">
              <span>Card surcharge</span>
              <span className="tabular-nums">+{cs()}{safeMoney(surcharge)}</span>
            </div>
          )}
          {/* iter334ad — B3 Invoice Breakdown lines. Credit Applied
              renders BEFORE the total so the PDF's total reflects the
              gross owed while the breakdown block below shows the
              actual cash charged vs credit applied. Only renders
              non-zero values. */}
          {Number(invoice.breakdown?.credit_applied || 0) > 0 && (
            <div className="flex justify-between text-emerald-700" data-testid="invoice-print-credit-applied">
              <span>Credit applied</span>
              <span className="tabular-nums">−{cs()}{safeMoney(invoice.breakdown.credit_applied)}</span>
            </div>
          )}
          <div className="flex justify-between border-t border-slate-300 pt-3 font-bold text-slate-900 text-base">
            <span>Total</span><span className="tabular-nums">{cs()}{safeMoney(totalAmount)}</span>
          </div>
            </>
          )}
          {/* Refund breakdown — same base/surcharge contract as the
              invoice modal (iter335b). Prefers the /refund-breakdown
              endpoint; falls back to invoice fields before it loads. */}
          {Number(invoice.refunded_amount || 0) > 0 && ['paid', 'refunded', 'partially_refunded'].includes(invoice.status) && (() => {
            const b = printBreakdown;
            const method = String(invoice.last_refund_method || b?.refund_method || '').toLowerCase();
            const methodLabels = {
              stripe: 'to card', card: 'to card', link: 'to card',
              credit: 'as account credit', account_credit: 'as account credit',
              cash: 'as cash', check: 'by check',
              zelle: 'via Zelle', venmo: 'via Venmo', manual: 'manually', waived: 'waived',
            };
            const methodLabel = methodLabels[method] || (b?.is_card ? 'to card' : '');
            const baseFee = b ? Number(b.base_fee_charged || 0) : Number(invoice.amount || 0);
            const surchargeCharged = b ? Number(b.surcharge_charged || 0) : Number(invoice.surcharge_amount || 0);
            const baseRefunded = b ? Number(b.base_refunded || 0) : Number(invoice.refunded_amount || 0);
            const surchargeRefunded = b ? Number(b.surcharge_refunded || 0) : 0;
            const currentFee = b ? Number(b.current_fee || 0) : Math.max(0, baseFee - baseRefunded);
            const showSurcharge = surchargeCharged > 0.005 || surchargeRefunded > 0.005;
            return (
              <div
                className="mt-3 rounded-md border border-purple-200 bg-purple-50 px-3 py-2 text-sm"
                data-testid="invoice-print-refund-breakdown"
              >
                <div className="flex justify-between text-purple-900">
                  <span>Base fee</span>
                  <span className="tabular-nums">{cs()}{safeMoney(baseFee)}</span>
                </div>
                {showSurcharge && (
                  <div className="flex justify-between text-purple-900">
                    <span>Surcharge charged</span>
                    <span className="tabular-nums">{cs()}{safeMoney(surchargeCharged)}</span>
                  </div>
                )}
                <div className="flex justify-between text-purple-900 border-t border-purple-100 pt-1 mt-1">
                  <span>Refunded{methodLabel ? ` · ${methodLabel}` : ''}</span>
                  <span className="tabular-nums">−{cs()}{safeMoney(baseRefunded)}</span>
                </div>
                {showSurcharge && (
                  <div className="flex justify-between text-purple-900">
                    <span>Refunded surcharge</span>
                    <span className="tabular-nums">−{cs()}{safeMoney(surchargeRefunded)}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold text-purple-900 border-t border-purple-200 pt-1 mt-1">
                  <span>Current fee</span>
                  <span className="tabular-nums">{cs()}{safeMoney(currentFee)}</span>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Payment info */}
        {isPaid && (
          <div className="mt-10 border-t border-slate-200 pt-6">
            <p className="text-xs uppercase tracking-wider text-slate-500 mb-3">Payment Received</p>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {invoice.paid_at && (
                <div><p className="text-slate-500 text-xs">Paid On</p><p className="text-slate-900">{safeDate(invoice.paid_at, 'MMMM d, yyyy', businessTz)}</p></div>
              )}
              {invoice.payment_method && (
                <div><p className="text-slate-500 text-xs">Method</p><p className="text-slate-900 capitalize">{invoice.accounting?.payment_method || invoice.payment_method}</p></div>
              )}
              {invoice.stripe_payment_intent_id && (
                <div className="col-span-2"><p className="text-slate-500 text-xs">Stripe Payment Intent</p><p className="text-slate-900 font-mono text-xs">{invoice.stripe_payment_intent_id}</p></div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-16 pt-6 border-t border-slate-200 text-xs text-slate-500 text-center">
          <p>{tenantBusinessName}{tenantDomain ? ` · ${tenantDomain}` : ''}</p>
          <p className="mt-1">Thank you for your support!</p>
        </div>
      </div>

      {/* Print-only stylesheet */}
      <style>{`
        @media print {
          @page { margin: 0.5in; size: letter; }
          body { background: white; }
        }
      `}</style>
    </div>
  );
}
